import { Component } from '@theme/component';

/**
 * A testimonial carousel: swipeable via native horizontal scroll-snap
 * (same touch-swipe pattern as the product media gallery), with
 * prev/next arrows and dot navigation driving the scroll position.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [track]
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class TestimonialCarousel extends Component {
  #current = 0;
  #scrollRaf = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#updateDots(0);
    this.refs.track?.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.refs.track?.removeEventListener('scroll', this.#onScroll);
  }

  next() {
    this.#goTo(this.#current + 1);
  }

  previous() {
    this.#goTo(this.#current - 1);
  }

  /** @param {CustomEvent<{index: number}>} event */
  goTo(event) {
    const index = Number(event.target?.closest('[data-testimonial-dot]')?.dataset.index);
    if (!Number.isNaN(index)) this.#goTo(index);
  }

  #onScroll = () => {
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
    this.#scrollRaf = requestAnimationFrame(() => {
      const track = this.refs.track;
      const slides = this.refs.slides ?? [];
      if (!track || slides.length === 0) return;

      const index = Math.round(track.scrollLeft / track.clientWidth);
      this.#updateDots(Math.min(Math.max(index, 0), slides.length - 1));
    });
  };

  /** @param {number} index */
  #goTo(index) {
    const track = this.refs.track;
    const slides = this.refs.slides ?? [];
    if (!track || slides.length === 0) return;

    let relativeIndex = index % slides.length;
    if (relativeIndex < 0) relativeIndex += slides.length;

    track.scrollTo({ left: relativeIndex * track.clientWidth, behavior: 'smooth' });
    this.#updateDots(relativeIndex);
  }

  /** @param {number} index */
  #updateDots(index) {
    this.#current = index;
    this.querySelectorAll('[data-testimonial-dot]').forEach((dot, i) => {
      dot.setAttribute('aria-current', `${i === index}`);
    });
  }
}

if (!customElements.get('testimonial-carousel-component')) {
  customElements.define('testimonial-carousel-component', TestimonialCarousel);
}
